import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import axios from "axios";
import { useHistory } from "react-router-dom";
import web3 from "../../../Service/web3";
import "../../counter.css";
import "./hero.css";
import { colors } from "../../../../theme/colors";
import HeroStats from "./heroStates.js";
import HeroActions from "./heroActions";
import { AutoBetGlobe } from "./three";
import Ticker from "./ticker";
import heroBackground from "../../../../static/Images/heroBackground.png";
import {
  startMarqueeAnimation,
  startHeroBackgroundAnimation,
  startGlobeParallaxAnimation,
  startHeroEntryAnimation,
  cleanupAnimation,
  trustShineSx,
} from "../index.js";

const Hero = ({ item, bookies, lotteries, buytotal, draws, wins, variant }) => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winnerNotifications, setWinnerNotifications] = useState([]);
  const [statsReady, setStatsReady] = useState(false);

  const autoBetRef = useRef(null);
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);
  const globeRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_KEY;
  const history = useHistory();

  useEffect(() => {
    const animation = startMarqueeAnimation(autoBetRef);
    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    const bgAnimation = startHeroBackgroundAnimation(heroBgRef);
    const globeAnimation = startGlobeParallaxAnimation(globeRef);

    return () => {
      if (bgAnimation) bgAnimation.revert();
      if (globeAnimation) globeAnimation.revert();
    };
  }, []);

  useEffect(() => {
    let animation;

    const startAnimation = () => {
      animation = startHeroEntryAnimation(heroContentRef, () => {
        setStatsReady(true);
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(startAnimation);
    });

    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    axios.get(`${apiUrl}/news/getAllNews`).then((response) => {
      const newsData = response.data.result;
      const featuredNewsData = newsData.filter((news) => news.featured);
      setFeaturedNews(featuredNewsData);
    });

    const fetchData = async () => {
      try {
        const account = await web3.eth.getAccounts();
        let skip = 0;
        let allNotifications = [];
        let allWinnerNotifications = [];

        const fetchNotifications = async () => {
          const response = await fetch(
            `${apiUrl}/notifications?limit=10&skip=${skip}`,
            {
              method: "GET",
              headers: { Authorization: account[0] },
            }
          );

          if (response.ok) {
            const data = await response.json();

            const filteredNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryCreatedAdmin" ||
                notification.type === "LotteryCreated"
            );

            if (filteredNotifications.length > 0) {
              allNotifications = [
                ...allNotifications,
                ...filteredNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setNotifications(allNotifications);
            }

            const filteredWinnerNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryResult" ||
                notification.type === "SpinLotteryResult" ||
                notification.type === "LotteryWinner"
            );

            if (filteredWinnerNotifications.length > 0) {
              allWinnerNotifications = [
                ...allWinnerNotifications,
                ...filteredWinnerNotifications,
              ];
              skip += 10;
              await fetchNotifications();
            } else {
              setWinnerNotifications(allWinnerNotifications);
            }
          } else {
            console.error("Error fetching notifications:", response.error);
          }
        };

        await fetchNotifications();
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchData();
  }, []);

  function handleRedirect(notification) {
    switch (notification.type) {
      case "LotteryCreated":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      case "LotteryCreatedAdmin":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;
      default:
        break;
    }
  }

  return (
    <Container maxWidth={false} disableGutters sx={{ p: 0, pt: '80px' }}>
      <Ticker
        featuredNews={featuredNews}
        notifications={notifications}
        winnerNotifications={winnerNotifications}
        handleRedirect={handleRedirect}
      />

      <Box
        className="banner-section"
        sx={{
          position: "relative",
          minHeight: {
            xs: "calc(100svh - 60px)",
            sm: "calc(100dvh - 60px)",
            md: "calc(100dvh - 60px)",
          },
          height: {
            xs: "auto",
            sm: "calc(100dvh - 60px)",
            md: "calc(100dvh - 60px)",
          },
          overflow: "hidden",
          background: (theme) => theme.palette.background.default,
        }}
      >
        <Box
          ref={heroBgRef}
          className="hero-bg-layer"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            willChange: "transform",
          }}
        />

        <Box
          ref={globeRef}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
            willChange: "transform",
          }}
        >
          <AutoBetGlobe />
        </Box>

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            background: {
              xs: "rgba(255, 255, 255, 0.2)",
              sm: (theme) =>
                `linear-gradient(90deg, ${theme.custom.colors.alpha.cream[96]} 0%, ${theme.custom.colors.alpha.cream[88]} 32%, ${theme.custom.colors.alpha.cream[30]} 62%, ${theme.custom.colors.alpha.cream[5]} 100%)`,
            },
            backdropFilter: { xs: "blur(2px)", sm: "none" },
            WebkitBackdropFilter: { xs: "blur(2px)", sm: "none" },
            pointerEvents: "none",
          }}
        />

          <Box
            className="banner-content-area"
            sx={{
              position: "relative",
              zIndex: 2,
              height: {
                xs: "100%",
                sm: "auto",
                md: "auto",
              },
              display: "flex",
              alignItems: "stretch",
              flexDirection: "column",
              pt: "20px",
            }}
          >
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              px: {
                xs: 2,
                sm: 5,
                md: 6,
                lg: 6,
              },
              pt: 0,
              pb: {
                xs: 1,
                sm: 2,
                md: 3,
              },
              flex: {
                xs: 1,
                sm: "none",
                md: "none",
              },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              ref={heroContentRef}
              sx={{
                width: "100%",
                maxWidth: "900px",
                flex: {
                  xs: 1,
                  sm: "none",
                  md: "none",
                },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                className={`banner-content${
                  variant === "home" ? " home-hero" : ""
                }`}
                sx={{
                  width: "100%",
                  justifyContent: "flex-start",
                  flex: {
                    xs: 1,
                    sm: "none",
                    md: "none",
                  },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {variant === "home" && (
                  <Box
                    component="p"
                    className="home-kicker hero-entry-item"
                    sx={{
                      color: "#000",
                      textTransform: "uppercase",
                      fontSize: { xs: "8px", sm: "8px", md: "8px" },
                      whiteSpace: "nowrap",
                      fontWeight: 500,
                      letterSpacing: { xs: "0.7px", sm: "1.4px" },
                      display: "flex",
                      alignItems: "center",
                      gap: { xs: "6px", sm: "11px" },
                      margin: 0,
                      mb: { xs: "18px", md: 3 },
                      "&::before": {
                        content: '""',
                        width: { xs: "18px", sm: "30px" },
                        height: "2px",
                        borderRadius: "2px",
                        background: "#000",
                        flexShrink: 0,
                      },
                    }}
                  >
                    DECENTRALIZED LOTTERY & GAMING ECOSYSTEM
                  </Box>
                )}

                <Typography
                  component="h1"
                  className="title hero-entry-item"
                  sx={{
                    textAlign: "left",
                    fontWeight: 800,
                    color: (theme) => theme.palette.text.primary,
                    margin: 0,
                    mb: { xs: "18px", md: "24px" },
                    fontSize: {
                      xs: "3rem",
                      sm: "3.5rem",
                      md: "4rem",
                      lg: "4.5rem",
                    },
                    letterSpacing: "2.5px",
                    lineHeight: { xs: 0.98, md: 1 },
                    whiteSpace: { xs: "normal", sm: "nowrap" },
                  }}
                >
                  Play. Win.{" "}
                  <Box
                    component="span"
                    sx={{
                      ...trustShineSx(colors.primary.main),
                      fontSize: { xs: "1.4em", sm: "1.2em" },
                      fontWeight: 900,
                      WebkitTextStroke: `1.5px ${colors.primary.main}`,
                    }}
                  >
                    Trust.
                  </Box>
                </Typography>

                <Typography
                  component="p"
                  className="hero-entry-item"
                  sx={{
                    color: (theme) => ({
                      xs: theme.palette.text.primary,
                      sm: theme.palette.text.secondary,
                    }),
                    textShadow: (theme) => ({
                      xs: `0 0 4px ${theme.palette.background.default || '#ffffff'}, 0 0 1px ${theme.palette.background.default || '#ffffff'}`,
                      sm: "none",
                    }),
                    fontSize: { xs: "12px", sm: "8px", md: "8px" },
                    lineHeight: { xs: 1.55, md: 1.55 },
                    maxWidth: "800px",
                    margin: 0,
                    mb: { xs: "20px", md: "24px" },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: { xs: "none", sm: "inline" },
                    }}
                  >
                    AutoBET is a next-generation blockchain lottery and gaming
                    platform featuring fair, transparent and globally
                    accessible games like Powerball, Revolver, Pokies and more
                    — all powered by smart contracts.
                  </Box>

                  <Box
                    component="span"
                    sx={{
                      display: { xs: "inline", sm: "none" },
                    }}
                  >
                    AutoBET is a fair, transparent and globally accessible
                    lottery and gaming platform with games like Powerball,
                    Revolver, Pokies and more — powered by smart contracts.
                  </Box>
                </Typography>

                <Box className="hero-entry-item">
                  <HeroStats
                    bookies={bookies}
                    lotteries={lotteries}
                    buytotal={buytotal}
                    draws={draws}
                    wins={wins}
                    startAnimation={statsReady}
                  />
                </Box>

                {variant === "home" && (
                  <Box
                    className="hero-entry-item"
                    sx={{
                      mt: { xs: "20px", sm: 2, md: 2.5 },
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <HeroActions history={history} />
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </Container>
  );
};

export default Hero;
